import type {Session} from '../types/session'
import {useQuery, useMutation} from '@github-ui/react-query'
import type {Pull} from '../types/pull'
import type {AgentMention} from '../types/agent-mention'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {useTopLevelAgents} from '@github-ui/copilot-shared-ui/agents/use-top-level-agents'
import {hasModelsForVendors} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'

interface UseAgentMentionsParams {
  pull?: Pull
  sessions?: Session[]
  initialData?: AgentMention[]
  agentId?: number
}

type AgentMentionResponse = {
  pull_request_id: number
  agent_mentions: AgentMention[]
}

interface AgentMentionMutationCallbacks {
  onSuccess?: (data: unknown) => void
  onError?: () => void
}

export function useAgentMentions({pull, sessions = [], initialData = [], agentId}: UseAgentMentionsParams) {
  const {data: agentInfo} = useAgentInfo(agentId)
  const agentDisplayName = agentInfo?.display_name.toLowerCase() || 'copilot'

  const {data: validAgentNames = []} = useTopLevelAgents(pull?.repository_nwo)
  const {models = [], isLoading: modelsLoading, error: modelsError} = useAgentModels()

  // Filter out agents whose vendor has no available models.
  // Skip filtering while models are loading or on error to avoid incorrectly invalidating mentions.
  const validAgentNamesFiltered =
    modelsLoading || modelsError
      ? validAgentNames
      : validAgentNames.filter(agent => hasModelsForVendors(agent.model_vendors, models))

  const params = buildRequestParams(sessions)

  const fetchMentionsQuery = useQuery<AgentMention[]>({
    queryKey: ['agent-mentions', pull?.id, params],
    queryFn: async () => {
      if (!pull?.id) {
        return []
      }

      const response = await fetch(`/copilot/agent-sessions/pull-requests/${pull.id}/agent-mentions?${params}`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      if (!response.ok) {
        throw new Error('Failed to fetch pull request comments')
      }

      const data = (await response.json()) as AgentMentionResponse
      return data.agent_mentions
    },
    enabled: sessions.length > 0 && !!pull?.id,
    initialData,
    // Ensure existing mentions continue to show when fetching new ones
    placeholderData: previousData => previousData,
  })

  // Mutation for creating a new mention (comment)
  const createMentionMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!pull) {
        throw new Error('Cannot create mention without a pull request')
      }

      const isCopilot = agentDisplayName === 'copilot'

      const mentionPrefix = !isCopilot ? `@${agentDisplayName}[agent]` : `@${agentDisplayName}`

      const agentMentionWithSuffixMatch = text.match(/^@([\w-]+)\[agent\](?:\s|$)/i)
      const agentMentionWithoutSuffixMatch = text.match(/^@([\w-]+)(?:\s|$)/i)
      const startsWithCopilot = /^@copilot(?:\s|$)/i

      const isValidAgentName = (name: string) =>
        validAgentNamesFiltered.some(agent => agent.display_name.toLowerCase() === name.toLowerCase())

      const hasValidMention =
        (agentMentionWithSuffixMatch?.[1] && isValidAgentName(agentMentionWithSuffixMatch[1])) ||
        startsWithCopilot.test(text)

      if (!hasValidMention) {
        if (agentMentionWithoutSuffixMatch?.[1] && isValidAgentName(agentMentionWithoutSuffixMatch[1])) {
          // Valid agent but missing [agent] suffix - add it
          const agentName = agentMentionWithoutSuffixMatch[1]
          text = text.replace(new RegExp(`^@${agentName}`, 'i'), `@${agentName}[agent]`)
        } else {
          // No valid mention - prepend the correct agent
          text = `${mentionPrefix} ${text.trim()}`
        }
      }

      const result = await reactFetchJSON(
        `/${pull.repository_nwo}/pull/${pull.number}/page_data/create_timeline_comment`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: {text},
        },
      )

      const json = await result.json()

      if (!result.ok) {
        throw new Error('Failed to submit comment')
      }

      sendEvent('agent_sessions.task_view', {
        target: 'CREATE_COMMAND_POST_SESSION',
        pull_id: pull.id,
        pull_number: pull.number,
      })

      return json
    },
  })

  // Create a wrapper function to handle the callbacks properly
  const createMention = async (text: string, callbacks?: AgentMentionMutationCallbacks) => {
    try {
      const result = await createMentionMutation.mutateAsync(text)
      callbacks?.onSuccess?.(result)
      return result
    } catch (error) {
      callbacks?.onError?.()
      throw error // Re-throw to allow caller to handle
    }
  }

  return {
    ...fetchMentionsQuery,
    createMention,
  }
}

function buildRequestParams(sessions: Session[]) {
  const pull_request_comment_ids = sessions
    .filter(
      session =>
        session.event_type === 'pull_request_comment' ||
        session.event_type === 'pull_request_comment_non_copilot_pull_request',
    )
    .map(session => session.event_identifiers)
    .flat()
    .sort() // sorting (even lexicographically) ensures the query key is deterministic

  const pull_request_review_ids = sessions
    .filter(
      session =>
        session.event_type === 'pull_request_review' ||
        session.event_type === 'pull_request_review_non_copilot_pull_request',
    )
    .map(session => session.event_identifiers)
    .flat()
    .sort() // sorting (even lexicographically) ensures the query key is deterministic

  const params = new URLSearchParams()
  for (const id of pull_request_comment_ids) {
    if (id != null) {
      params.append('pull_request_comment_ids[]', id.toString())
    }
  }
  for (const id of pull_request_review_ids) {
    if (id != null) {
      params.append('pull_request_review_ids[]', id.toString())
    }
  }
  return params.toString()
}
